import { useCallback, useMemo } from "react";
import React from "react";

import { Rename } from "components/Prompts";
import { useActionHook, useClientParamsHooks, useModals, useNavigate, usePrompt } from "hooks";
import { useClientActions, useDealActions } from "store";

import { useClientWorkspaceCtx } from "../context/ClientWorkspace-context";

interface UseFolderHookParams {
  folder: {
    id: number;
    title: string;
    parent_id: number | null;
  };
  refetchHandler?: () => Promise<void>;
}

export const useFolderHook = ({ folder, refetchHandler }: UseFolderHookParams) => {
  const {
    createClientWorkspaceFolderAction,
    updateClientWorkspaceFolderAction,
    uploadClientWorkspaceFileAction
  } = useClientActions();
  const { deleteFolderAction } = useDealActions();
  const { clientId } = useClientParamsHooks();
  const navigate = useNavigate();
  const { patchFolder } = useClientWorkspaceCtx();

  const { createModal, deleteModal, closeDeleteModal, errorModal, filesUploadModal } = useModals();
  const openPrompt = usePrompt();

  const isRoot = folder.id === -1;

  const { action: updateFolder } = useActionHook(updateClientWorkspaceFolderAction);

  const onCreateNewFolder = useMemo(
    () => () =>
      createModal({
        action: async (name: string) => {
          const newFolder = await createClientWorkspaceFolderAction({
            clientId,
            title: name,
            parent_id: isRoot ? null : folder.id
          });
          await refetchHandler?.();
          navigate({
            route: "clientWorkspaceFolder",
            params: { client_id: clientId, folder_id: newFolder.id },
            search: { section: newFolder.id }
          });
        },
        title: "Add Folder",
        submitButtonTitle: "Add"
      }),
    [isRoot, clientId, folder.id, createModal, createClientWorkspaceFolderAction, refetchHandler, navigate]
  );

  const onFileUpload = useCallback(async () => {
    filesUploadModal({
      clientId,
      folderId: folder?.id === -1 ? undefined : folder?.id,
      onSuccessCallback: () => refetchHandler?.() ?? Promise.resolve()
    });
  }, [clientId, folder?.id, refetchHandler, filesUploadModal]);

  const onDropFiles = useCallback(
    async (files: File[]) => {
      if (!files.length) return;
      const section_id = folder?.id === -1 ? null : folder?.id;
      const outcomes = await Promise.allSettled(
        files.map((file) => uploadClientWorkspaceFileAction({ clientId, file, section_id }))
      );
      await refetchHandler?.();
      const failed = outcomes.filter((outcome) => outcome.status === "rejected").length;
      if (failed) {
        errorModal({ msg: `Failed to upload ${failed} of ${files.length} file${files.length === 1 ? "" : "s"}.` });
      }
    },
    [clientId, folder?.id, refetchHandler, uploadClientWorkspaceFileAction, errorModal]
  );

  const onRenameFolder = useCallback(
    async (menuTriggerRef: React.RefObject<HTMLButtonElement>, onMenuClose: () => void) => {
      if (isRoot || !folder) return;
      const trigger = menuTriggerRef.current;
      if (!trigger) return;

      const triggerRect = trigger.getBoundingClientRect();
      const anchorPosition = {
        top: Math.round(triggerRect.bottom),
        left: Math.round(triggerRect.left)
      };

      onMenuClose();

      const title = await openPrompt({
        Component: Rename,
        componentType: "popover",
        anchorPosition,
        anchorOrigin: { vertical: "bottom", horizontal: "left" },
        transformOrigin: { vertical: "top", horizontal: "left" },
        props: { title: folder.title }
      });
      if (title) {
        await updateFolder({ clientId, folderId: folder.id, title });
        // Patch just this folder in the store instead of a full refetch.
        patchFolder(folder.id, { title });
      }
    },
    [isRoot, folder, openPrompt, updateFolder, clientId, patchFolder]
  );

  const handleDelete = async (resolve?: Function) => {
    if (isRoot || !folder) return;
    try {
      // Client folders have no deal — dealId 0 routes to the global-workspace folders endpoint.
      await deleteFolderAction({ dealId: 0, folderId: folder.id });
      await refetchHandler?.();
      resolve?.(true);
      closeDeleteModal();
      const parentId = folder.parent_id;
      if (parentId) {
        navigate({
          route: "clientWorkspaceFolder",
          params: { client_id: clientId, folder_id: parentId },
          search: { section: parentId }
        });
      } else {
        navigate({ route: "clientWorkspace", params: { client_id: clientId }, setQueriesMethod: "drop" });
      }
    } catch (e) {
      errorModal({ msg: (e as Error).message });
    }
  };

  const onDeleteFolder = useMemo(
    () =>
      isRoot || !folder
        ? undefined
        : () =>
            deleteModal({
              element: "Delete folder permanently?",
              message: "This cannot be undone. All contents will be deleted.",
              onPressSuccess: "Delete permanently",
              action: async () => handleDelete()
            }),
    [isRoot, folder, deleteModal, handleDelete]
  );

  return {
    onCreateNewFolder,
    onFileUpload,
    onDropFiles,
    onRenameFolder,
    onDeleteFolder
  };
};
